import React from "react";
import { CircleAlert, ClockAlert, LoaderCircle } from "lucide-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InvalidSessionPage } from "@/features/kyc/components/InvalidSessionPage";
import { KycFlow } from "@/features/kyc/flow/KycFlow";
import {
  KycSessionProvider,
  useKycSession,
} from "@/features/kyc/flow/KycSessionProvider";

export default function VerificationPage() {
  const { sessionId } = useParams();

  if (!sessionId) return <InvalidSessionPage />;

  return (
    <KycSessionProvider sessionId={sessionId}>
      <VerificationSession />
    </KycSessionProvider>
  );
}

function VerificationSession() {
  const { t } = useTranslation();
  const { sessionId, session, siteConfig, isLoading, isError, error, retry } =
    useKycSession();

  if (isLoading) {
    return (
      <SessionState
        icon={
          <LoaderCircle className="size-6 animate-spin" aria-hidden="true" />
        }
        title={t("kyc.portal.loadingTitle")}
        text={t("kyc.portal.loadingText")}
      />
    );
  }

  if (isError) {
    const status = error?.response?.status;

    if (status === 404 || status === 400) return <InvalidSessionPage />;

    if (status === 410) {
      return (
        <SessionState
          icon={<ClockAlert className="size-6" aria-hidden="true" />}
          title={t("kyc.portal.expiredTitle")}
          text={t("kyc.portal.expiredText")}
        />
      );
    }

    return (
      <SessionState
        icon={<CircleAlert className="size-6" aria-hidden="true" />}
        title={t("kyc.portal.unavailableTitle")}
        text={t("kyc.portal.unavailableText")}
        action={
          <button
            type="button"
            onClick={() => retry()}
            className="mt-7 inline-flex min-h-12 w-full max-w-sm items-center justify-center rounded-lg bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800 active:scale-[0.99]"
          >
            {t("common.retry")}
          </button>
        }
      />
    );
  }

  return (
    <KycFlow session={session} sessionId={sessionId} siteConfig={siteConfig} />
  );
}

function SessionState({ icon, title, text, action }) {
  return (
    <section className="kyc-flow-card kyc-surface mx-auto my-auto flex w-[calc(100%-1.5rem)] max-w-3xl flex-col justify-center rounded-3xl border border-white/10 bg-white px-6 py-14 text-center sm:px-10">
      <span className="mx-auto flex size-16 items-center justify-center rounded-2xl bg-teal-50 text-teal-800 ring-1 ring-teal-100">
        {icon}
      </span>
      <h1 className="mt-7 text-[1.75rem] font-semibold leading-tight tracking-[-0.035em] text-slate-950">
        {title}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-600">
        {text}
      </p>
      {action}
    </section>
  );
}
